class HokayzzVaultBuilder {
  constructor(root) {
    this.root = root;
    this.maximum = Number(root.dataset.maxSelections || 5);
    this.variantId = Number(root.dataset.variantId || 0);

    this.cards = Array.from(root.querySelectorAll("[data-vault-card]"));
    this.submitButton = root.querySelector("[data-vault-submit]");
    this.selectedList = root.querySelector("[data-selected-list]");
    this.countElement = root.querySelector("[data-selection-count]");
    this.mobileCountElement = root.querySelector("[data-mobile-count]");
    this.progressDots = Array.from(root.querySelectorAll("[data-progress-dot]"));
    this.messageElement = root.querySelector("[data-vault-message]");
    this.errorElement = root.querySelector("[data-submit-error]");
    this.summary = root.querySelector("[data-vault-summary]");
    this.mobileSummaryButton = root.querySelector("[data-mobile-summary-button]");

    this.selections = [];

    this.bindEvents();
    this.render();
  }

  bindEvents() {
    this.cards.forEach((card) => {
      card.addEventListener("click", (event) => {
        if (event.target.closest("[data-details-link]")) return;
        this.addSelection(card);
      });

      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        if (event.target.closest("[data-details-link]")) return;

        event.preventDefault();
        this.addSelection(card);
      });
    });

    this.submitButton?.addEventListener("click", () => this.addVaultToCart());

    this.mobileSummaryButton?.addEventListener("click", () => {
      this.summary?.classList.toggle("is-open");
    });
  }

  addSelection(card) {
  if (this.selections.length >= this.maximum) {
    this.showMessage(
      "Your Discovery Vault already contains five fragrances. Remove one to add another.",
      true
    );
    return;
  }

  this.selections.push({
    id: card.dataset.productId,
    title: card.dataset.productTitle,
  });

  const count = this.selections.length;

  const messages = {
    1: "1 of 5 selected — Four more to go.",
    2: "2 of 5 selected — Keep exploring.",
    3: "3 of 5 selected — Two more fragrances to choose.",
    4: "4 of 5 selected — Just one more fragrance.",
    5: "5 of 5 selected — Your Discovery Vault is ready."
  };

  this.showMessage(messages[count], false);
  this.render();
}

  removeSelection(index) {
  this.selections.splice(index, 1);

  const count = this.selections.length;

  const messages = {
    0: "Select five fragrances to complete your Vault.",
    1: "1 of 5 selected — Four more to go.",
    2: "2 of 5 selected — Keep exploring.",
    3: "3 of 5 selected — Two more fragrances to choose.",
    4: "4 of 5 selected — Just one more fragrance."
  };

  this.showMessage(messages[count], false);
  this.render();
}

  getProductQuantity(productId) {
    return this.selections.filter((selection) => selection.id === productId).length;
  }

  renderCards() {
    this.cards.forEach((card) => {
      const quantity = this.getProductQuantity(card.dataset.productId);
      const quantityBadge = card.querySelector("[data-card-quantity]");
      const status = card.querySelector("[data-card-status]");

      card.classList.toggle("is-selected", quantity > 0);
      card.setAttribute("aria-pressed", quantity > 0 ? "true" : "false");

      if (quantityBadge) {
        quantityBadge.hidden = quantity === 0;
        quantityBadge.textContent = quantity;
      }

      if (status) {
        status.textContent =
  quantity > 0
    ? quantity === 1
      ? "✓ Added"
      : `✓ Added × ${quantity}`
    : "Add to Vault";
      }
    });
  }

  renderProgress() {
    this.progressDots.forEach((dot, index) => {
      dot.classList.toggle("is-filled", index < this.selections.length);
    });

    if (this.countElement) {
      this.countElement.textContent = String(this.selections.length);
    }

    if (this.mobileCountElement) {
      this.mobileCountElement.textContent = String(this.selections.length);
    }
  }

  renderSelectedList() {
  if (!this.selectedList) return;

  this.selectedList.innerHTML = "";

  for (let index = 0; index < this.maximum; index += 1) {
    const selection = this.selections[index];
    const listItem = document.createElement("li");

    if (!selection) {
      listItem.className = "hok-vault-selected__empty";
      listItem.innerHTML = `
        <span>Selection ${index + 1}</span>
        <em>Waiting...</em>
      `;
    } else {
      listItem.className = "hok-vault-selected__chosen";

      const name = document.createElement("span");
      name.className = "hok-vault-selected__name";
      name.textContent = selection.title;

      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className = "hok-vault-remove";
      removeButton.setAttribute(
        "aria-label",
        `Remove ${selection.title} from your Discovery Vault`
      );
      removeButton.textContent = "×";
      removeButton.addEventListener("click", (event) => {
        event.stopPropagation();
        this.removeSelection(index);
      });

      listItem.append(name, removeButton);
    }

    this.selectedList.appendChild(listItem);
  }
}

  renderSubmitButton() {
    if (!this.submitButton) return;

    const complete =
      this.selections.length === this.maximum &&
      Number.isInteger(this.variantId) &&
      this.variantId > 0;

    this.submitButton.disabled = !complete;
    this.submitButton.textContent = complete
      ? "Build My Discovery Vault"
      : "Complete Your Selection";
  }

  render() {
    this.renderCards();
    this.renderProgress();
    this.renderSelectedList();
    this.renderSubmitButton();
  }

  showMessage(message, isError = false) {
    if (!this.messageElement) return;

    this.messageElement.textContent = message;
    this.messageElement.classList.toggle("is-error", isError);
  }

  async addVaultToCart() {
    if (
      this.selections.length !== this.maximum ||
      !Number.isInteger(this.variantId) ||
      this.variantId <= 0
    ) {
      return;
    }

    this.submitButton.disabled = true;
    this.submitButton.textContent = "Adding Your Vault…";

    if (this.errorElement) {
      this.errorElement.hidden = true;
      this.errorElement.textContent = "";
    }

    const properties = {};

    this.selections.forEach((selection, index) => {
      properties[`Fragrance ${index + 1}`] = selection.title;
    });

    properties["Vault Selection"] = this.selections
      .map((selection) => selection.title)
      .join(", ");

    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || "/"}cart/add.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: this.variantId,
                quantity: 1,
                properties,
              },
            ],
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || "The Discovery Vault could not be added.");
      }

      window.location.href = `${window.Shopify?.routes?.root || "/"}cart`;
    } catch (error) {
      if (this.errorElement) {
        this.errorElement.hidden = false;
        this.errorElement.textContent =
          error.message || "Something went wrong. Please try again.";
      }

      this.submitButton.disabled = false;
      this.submitButton.textContent = "Build My Discovery Vault";
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-vault-builder]").forEach((root) => {
    new HokayzzVaultBuilder(root);
  });
});